import * as React from 'react'
import { TransportProvider } from '@connectrpc/connect-query'
import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { useRouteChangeMiddleware } from '@modules/route/routeUtils'
import { useSentryRouteName } from '@modules/route/sentryRouteName'
import { safeInitializeGtm } from '@modules/utils/gtm'
import { createSentryBaseOptions } from '@modules/utils/sentryFilters'

import { env, gtmId, sentryDns } from '@admin/config'
import { AppStateContainer } from '@admin/context/AppStateContainer'
import { queryClient, transport } from '@admin/request/client'

if (typeof window !== 'undefined') {
  if (sentryDns)
    Sentry.init({
      dsn: sentryDns,
      environment: env,
      // 送信データの範囲（dataCollection）と送信前フィルタは 3 アプリで共通。個別に書くと
      // 書き忘れても型・lint・テストのどれも落ちないまま、そのアプリだけ SDK の既定
      // （v11 は IP アドレス・Cookie・本文を収集する側）に戻るため、必ずここから展開する
      ...createSentryBaseOptions(),
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      // Performance Monitoring
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/admin.iesapuri.com/,
        /^https:\/\/admin.stg.iesapuri.com/,
        /^https:\/\/api.iesapuri.com/,
        /^https:\/\/api.stg.iesapuri.com/,
      ],
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    })

  if (gtmId) {
    safeInitializeGtm(gtmId)
  }
}

interface Props {
  children: React.ReactNode
}

export const CustomProvider: React.FC<Props> = ({ children }) => {
  return <AppStateContainer.Provider>{children}</AppStateContainer.Provider>
}

// CustomProviderに移すとなぜかペンディングでクエリーが止まる
export const ConnectProvider = ({ children }: { children: React.ReactNode }) => {
  useRouteChangeMiddleware()
  // SDK v11 はルートパターンを知らないと計測の名前が `Pageload` / `Navigation` に潰れるため、
  // ルートパターンを明示的に渡す（@modules/route/sentryRouteName）
  useSentryRouteName()
  return (
    <TransportProvider transport={transport}>
      <QueryClientProvider client={queryClient}>
        {env === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
        {children}
      </QueryClientProvider>
    </TransportProvider>
  )
}
